import { useEffect, type ComponentProps } from "react";
import Button from "../../../shared/components/Button";
import ChallengeStatusBadge from "../../../shared/components/ChallengeStatusBadge";
import { siteTexts } from "../../../shared/i18n/siteTexts";
import ChallengeResultsView from "../../challenges/components/ChallengeResultsView";
import type { ChallengeDetail } from "../../challenges/types/challengeAdmin";
import type { ChallengeModalMode } from "../../challenges/types/challengeDraft";
import { challengeTypeLabel } from "../../challenges/utils/challengeLabels";
import AdminChallengeEditorPanel from "./AdminChallengeEditorPanel";
import AdminFinishedChallengePanel from "./AdminFinishedChallengePanel";

type AdminChallengeModalPanelProps = {
  challengeDetail: ChallengeDetail;
  challengeIdFromRoute?: number;
  challengeModalMode: ChallengeModalMode;
  editorPanelProps: ComponentProps<typeof AdminChallengeEditorPanel>;
  finishedPanelProps: ComponentProps<typeof AdminFinishedChallengePanel>;
  onBackToEdit: () => void;
  onClose: () => void;
};

function resolveChallengeTypeForBadge(challengeDetail: ChallengeDetail) {
  const type = String(challengeDetail.challengeType || "")
    .trim()
    .toLowerCase();
  if (type === "super8") return "super8";
  if (type === "poco") return "poco";
  if (type === "tournament") return "tournament";
  return "challenge";
}

export default function AdminChallengeModalPanel({
  challengeDetail,
  challengeIdFromRoute,
  challengeModalMode,
  editorPanelProps,
  finishedPanelProps,
  onBackToEdit,
  onClose,
}: AdminChallengeModalPanelProps) {
  const t = siteTexts.admin;
  const isTournamentChallenge =
    String(challengeDetail.challengeType || "")
      .trim()
      .toLowerCase() === "tournament";

  useEffect(() => {
    if (challengeIdFromRoute) return undefined;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        if (document.querySelector(".admin-section-modal-backdrop")) return;
        event.preventDefault();
        onClose();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [challengeIdFromRoute, onClose]);

  return (
    <div
      className={
        challengeIdFromRoute
          ? "admin-route-challenge"
          : "admin-modal-backdrop admin-modal-backdrop--challenge-editor"
      }
      role={challengeIdFromRoute ? undefined : "dialog"}
      aria-modal={challengeIdFromRoute ? undefined : "true"}
      aria-label={t.modal.challengeDetailsTitle}
    >
      <div
        className={`admin-modal admin-modal-wide${
          challengeIdFromRoute ? " admin-route-challenge-panel" : ""
        }`}
      >
        {!challengeIdFromRoute && (
          <div className="admin-modal-header">
            <h3>
              {challengeDetail.challengeName}
              <span className="admin-challenge-type-badge">
                {challengeTypeLabel(resolveChallengeTypeForBadge(challengeDetail))}
              </span>
              {" · "}
              <ChallengeStatusBadge status={challengeDetail.status} />
            </h3>
            <button
              type="button"
              className="admin-modal-icon-close"
              onClick={onClose}
              aria-label={siteTexts.common.close}
              title={siteTexts.common.close}
            >
              <span className="material-symbols-outlined" aria-hidden="true">
                close
              </span>
            </button>
          </div>
        )}

        {challengeModalMode === "results" ? (
          <div className="admin-results-container">
            <ChallengeResultsView challenge={challengeDetail} compact />
            {challengeDetail.status === "finished" && (
              <div className="admin-actions-row">
                <Button type="button" variant="primary" onClick={onBackToEdit}>
                  {t.historyList.editResults}
                </Button>
              </div>
            )}
          </div>
        ) : challengeDetail.status === "finished" && isTournamentChallenge ? (
          <AdminFinishedChallengePanel {...finishedPanelProps} />
        ) : (
          <AdminChallengeEditorPanel {...editorPanelProps} />
        )}
      </div>
    </div>
  );
}
